import React, { useState } from 'react';
import { useMap } from '../context/MapContext';
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';

import Tooltip from '@mui/material/Tooltip';
import Box from '@mui/material/Box';
import CloseIcon from '@mui/icons-material/Close';
import LogoutIcon from '@mui/icons-material/Logout';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import PictureAsPdf from '@mui/icons-material/PictureAsPdf';
import {
  Assessment,
  Mouse,
  Straighten
} from '@mui/icons-material';

import MapPdfExport from './MapPdfExport';
import FeatureSearch, { FeatureIndexItem } from './FeatureSearch';
import MauritaniaIcon from './MauritaniaIcon';
import FileImport from './FileImport';
import PetitMinBeta from './PetitMinBeta';
import LayersIcon from '@mui/icons-material/Layers'; // Import LayersIcon

interface HeaderProps {
  featureIndex: FeatureIndexItem[];
  onFeatureSelect: (feature: FeatureIndexItem) => void;
  onAboutClick: () => void;
  onMenuSelect: (item: string) => void;
  measureActive: boolean;
  onToggleMeasure: () => void;
  measureMode: 'line' | 'polyline' | 'area';
  onMeasureModeChange: (mode: 'line' | 'polyline' | 'area') => void;
  findXYActive: boolean;
  onToggleFindXY: () => void;
  importedFileName?: string | null;
  onClearCsv?: () => void;
  labelsVisible: boolean;
  onToggleLabels: (checked: boolean) => void;
  onLogout: () => void;
  userEmail: string;
  permanentLayers: Array<{ id: string; name: string }>;
  onHomeClick: () => void;
  handleFileImport: (file: File) => void;
  onAdminClick?: () => void;
  onDashboardClick?: () => void;
  isAdmin?: boolean;
  pdfExportOpen: boolean;
  setPdfExportOpen: (open: boolean) => void;
  mapContainerRef: React.RefObject<HTMLDivElement>;
  measureResults: Array<{ distance?: number; area?: number; coordinates: number[][] }>;
  pinnedPoints: Array<{ lat: number; lng: number; label?: string }>;
  scrollZoomEnabled: boolean;
  onToggleScrollZoom: () => void;
  onLayerActionClick?: () => void;
}

const Header: React.FC<HeaderProps> = ({
  onAboutClick,
  featureIndex,
  onFeatureSelect,
  onMenuSelect,
  measureActive,
  onToggleMeasure,
  measureMode,
  onMeasureModeChange,
  findXYActive,
  onToggleFindXY,
  importedFileName,
  onClearCsv,
  labelsVisible,
  onToggleLabels,
  onLogout,
  userEmail,
  permanentLayers,
  onHomeClick,
  handleFileImport,
  onAdminClick,
  onDashboardClick,
  isAdmin,
  pdfExportOpen,
  setPdfExportOpen,
  mapContainerRef,
  measureResults,
  pinnedPoints,
  scrollZoomEnabled,
  onToggleScrollZoom,
  onLayerActionClick,
}: HeaderProps) => {
  const importRef = React.useRef<{ open: () => void }>(null);
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const [importSubmenuOpen, setImportSubmenuOpen] = React.useState(false);
  const [showPetitMinBeta, setShowPetitMinBeta] = useState(false);

  const { visibleLayers, toggleLayer } = useMap();

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => setAnchorEl(event.currentTarget);
  const handleMenuClose = () => setAnchorEl(null);

  const handleMenuItemClick = (action: string) => {
    handleMenuClose();
    setImportSubmenuOpen(false);
    switch (action) {
      case 'about':
        onAboutClick();
        break;
      case 'admin':
        onAdminClick?.();
        break;
      case 'dashboard':
        onDashboardClick?.();
        break;
      case 'logout':
        onLogout();
        break;
      case 'pdf-export':
        setPdfExportOpen(true);
        break;
      case 'import-csv':
      case 'import-kml':
      case 'import-gpx':
      case 'import-shapefile':
        if (importRef.current) importRef.current.open();
        break;
    }
  };

  return (
    <>
      <AppBar
        position="fixed"
        sx={{
          background: 'rgba(24,29,38,0.97)',
          boxShadow: '0 2px 6px rgba(0,0,0,0.1)',
          height: '57px',
          minHeight: '57px !important',
          zIndex: 1301,
          display: 'flex',
          alignItems: 'center',
          '& .MuiToolbar-root': {
            minHeight: '57px !important',
            height: '57px !important',
            width: '100%',
            padding: '0 !important',
            display: 'flex',
            alignItems: 'center'
          },
          '& .MuiButton-root, & .MuiIconButton-root, & .MuiTypography-root': {
            fontSize: '24px', // Increased from 16px proportionally
          },
          // Keep search bar font size normal
          '& .feature-search-container .MuiInputBase-root': {
            fontSize: '19.5px', // Increased from 13px proportionally
          }
        }}
      >
        <Toolbar disableGutters sx={{ height: '57px', alignItems: 'center', px: { xs: 0.75, sm: 1.5 } }}>
          {/* Left: App Name */}
          <Box
            sx={{ display: 'flex', alignItems: 'center', pl: { xs: 0.75, sm: 1.5 }, cursor: 'pointer', userSelect: 'none' }}
            onClick={onHomeClick}
            tabIndex={0}
            role="button"
            aria-label="Vue initiale : MAADEN CARTO"
            onKeyPress={e => { if (e.key === 'Enter' || e.key === ' ') onHomeClick(); }}
          >
            <MauritaniaIcon style={{ fontSize: 30, marginRight: 12, color: '#fff' }} />
            <Typography
              variant="h6"
              sx={{
                fontFamily: 'Louguiya, sans-serif',
                fontWeight: 800,
                fontSize: { xs: '1.38rem', sm: '1.58rem' },
                color: '#fff',
                lineHeight: 1,
                display: 'flex',
                alignItems: 'center',
                maxWidth: { xs: 135, sm: 270 },
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                pr: { xs: 0.75, sm: 1.5 }
              }}
            >
              <Box sx={{ display: { xs: 'inline', sm: 'none' } }}>MAADEN</Box>
              <Box sx={{ display: { xs: 'none', sm: 'inline' } }}>MAADEN CARTO</Box>
            </Typography>
          </Box>
          {/* Center Controls */}
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.6,
            position: 'absolute',
            left: '50%',
            transform: 'translateX(-50%)',
            ml: 2 // Reduced to accommodate new buttons
          }}>
            {/* XY Button */}
            <Box
              onClick={onToggleFindXY}
              sx={{
                px: 1.5,
                py: 0,
                cursor: 'pointer',
                borderRadius: '6px',
                backgroundColor: findXYActive ? 'rgba(33,150,243,0.2)' : 'transparent',
                border: findXYActive ? '1px solid rgba(33,150,243,0.5)' : '1px solid transparent',
                color: findXYActive ? '#90caf9' : '#fff',
                fontSize: '1.23rem',
                fontFamily: 'Louguiya, sans-serif',
                fontWeight: 500,
                lineHeight: 1,
                display: 'flex',
                alignItems: 'center',
                height: '57px',
                '&:hover': { backgroundColor: 'rgba(255,255,255,0.08)', color: '#90caf9' }
              }}
            >
              XY
            </Box>

            {/* Measure Button */}
            <Tooltip title="Measure" arrow>
              <IconButton
                onClick={onToggleMeasure}
                sx={{
                  color: measureActive ? '#90caf9' : '#fff',
                  backgroundColor: measureActive ? 'rgba(33,150,243,0.2)' : 'transparent',
                  border: measureActive ? '1px solid rgba(33,150,243,0.5)' : '1px solid transparent',
                  borderRadius: '6px',
                  width: '40px',
                  height: '40px',
                  '&:hover': {
                    backgroundColor: 'rgba(255,255,255,0.08)',
                    color: '#90caf9'
                  }
                }}
                aria-label="Toggle measure tool"
              >
                <Straighten fontSize="small" />
              </IconButton>
            </Tooltip>

            {/* Scroll Zoom Button */}
            <Tooltip title="Scroll Zoom" arrow>
              <IconButton
                onClick={onToggleScrollZoom}
                sx={{
                  color: scrollZoomEnabled ? '#90caf9' : '#fff',
                  backgroundColor: scrollZoomEnabled ? 'rgba(33,150,243,0.2)' : 'transparent',
                  border: scrollZoomEnabled ? '1px solid rgba(33,150,243,0.5)' : '1px solid transparent',
                  borderRadius: '6px',
                  width: '40px',
                  height: '40px',
                  '&:hover': {
                    backgroundColor: 'rgba(255,255,255,0.08)',
                    color: '#90caf9'
                  }
                }}
                aria-label="Toggle scroll zoom"
              >
                <Mouse fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>

          {/* Right Controls */}
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.6,
            position: 'absolute',
            right: 0,
            pr: 1.5
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Box sx={{ minWidth: 270, maxWidth: 390 }}>
                <FeatureSearch featureIndex={featureIndex} onSelect={onFeatureSelect} />
              </Box>
              <FileImport
                ref={importRef}
                accept=".kml,.gpx,.csv,.geojson,.json,.shp,.zip"
                onFileSelected={handleFileImport}
                label="Import File"
                enableGlobalDrop={false}
              />
            </Box>

            {importedFileName && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  background: 'rgba(40,40,40,0.75)',
                  border: '1px solid rgba(255,255,255,0.2)',
                  borderRadius: '15px',
                  px: 0.9,
                  gap: 0.45
                }}
              >
                <Typography variant="caption" sx={{ color: '#eee', fontWeight: 500, fontSize: '1.05rem' }}>
                  {importedFileName}
                </Typography>
                <IconButton size="small" onClick={onClearCsv}>
                  <CloseIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </Box>
            )}

            {/* PDF Export Button */}
            <Tooltip title="Export PDF">
              <IconButton
                onClick={() => setPdfExportOpen(true)}
                sx={{
                  color: '#fff',
                  p: 0.8,
                  borderRadius: '6px',
                  '&:hover': { backgroundColor: 'rgba(255,255,255,0.1)' }
                }}
              >
                <PictureAsPdf sx={{ fontSize: '1.2rem' }} />
              </IconButton>
            </Tooltip>

            {/* Petit Min Beta Button */}
            <Tooltip title="Petit Min Beta - Nouvelle demande de polygone">
              <IconButton
                onClick={() => setShowPetitMinBeta(true)}
                sx={{
                  color: '#ffc107',
                  p: 0.8,
                  borderRadius: '6px',
                  border: '1px solid #ffc107',
                  '&:hover': { backgroundColor: 'rgba(255, 193, 7, 0.1)', borderColor: '#ffc107' }
                }}
              >
                <Assessment sx={{ fontSize: '1.2rem' }} />
              </IconButton>
            </Tooltip>

            {/* Layer Manager Button */}
            <Tooltip title="Layer Manager">
              <IconButton
                onClick={onLayerActionClick}
                sx={{
                  color: '#fff',
                  p: 0.8,
                  borderRadius: '6px',
                  '&:hover': { backgroundColor: 'rgba(255,255,255,0.1)' }
                }}
              >
                <LayersIcon sx={{ fontSize: '1.2rem' }} />
              </IconButton>
            </Tooltip>

            <Menu
              anchorEl={anchorEl}
              open={open}
              onClose={handleMenuClose}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              PaperProps={{
                sx: {
                  minWidth: 165,
                  mt: 0.75,
                  bgcolor: 'rgba(0,0,0,0.82)',
                  color: '#fff',
                  border: '1px solid rgba(255,255,255,0.1)'
                }
              }}
            >
              <MenuItem onClick={() => handleMenuItemClick('about')} sx={{ fontSize: '0.9rem', py: 1 }}>
                <InfoOutlinedIcon sx={{ mr: 1, fontSize: '1.1rem' }} />
                À propos
              </MenuItem>

              {/* Import Menu Item */}
              <MenuItem
                onClick={() => setImportSubmenuOpen(!importSubmenuOpen)}
                sx={{ fontSize: '0.9rem', py: 1 }}
              >
                <FileUploadIcon sx={{ mr: 1, fontSize: '1.1rem' }} />
                Import
                <ExpandMoreIcon sx={{ ml: 'auto', fontSize: '1rem' }} />
              </MenuItem>

              {/* Import Submenu */}
              {importSubmenuOpen && (
                <Box sx={{ pl: 3, bgcolor: 'rgba(0,0,0,0.3)' }}>
                  <MenuItem onClick={() => handleMenuItemClick('import-csv')} sx={{ fontSize: '0.85rem', py: 0.8 }}>
                    CSV
                  </MenuItem>
                  <MenuItem onClick={() => handleMenuItemClick('import-kml')} sx={{ fontSize: '0.85rem', py: 0.8 }}>
                    KML
                  </MenuItem>
                  <MenuItem onClick={() => handleMenuItemClick('import-gpx')} sx={{ fontSize: '0.85rem', py: 0.8 }}>
                    GPX
                  </MenuItem>
                  <MenuItem onClick={() => handleMenuItemClick('import-shapefile')} sx={{ fontSize: '0.85rem', py: 0.8 }}>
                    Shapefile
                  </MenuItem>
                </Box>
              )}

              {isAdmin && (
                <MenuItem onClick={() => handleMenuItemClick('admin')} sx={{ fontSize: '0.9rem', py: 1 }}>
                  <AdminPanelSettingsIcon sx={{ mr: 1, fontSize: '1.1rem' }} />
                  Administration
                </MenuItem>
              )}
              {isAdmin && (
                <MenuItem onClick={() => handleMenuItemClick('dashboard')} sx={{ fontSize: '0.9rem', py: 1 }}>
                  <Assessment sx={{ mr: 1, fontSize: '1.1rem' }} />
                  Dashboard
                </MenuItem>
              )}
              <MenuItem onClick={() => handleMenuItemClick('logout')} sx={{ fontSize: '0.9rem', py: 1 }}>
                <LogoutIcon sx={{ mr: 1, fontSize: '1.1rem' }} />
                Déconnexion
              </MenuItem>
            </Menu>

            <IconButton
              onClick={handleMenuOpen}
              sx={{
                color: '#fff',
                p: 0.8,
                borderRadius: '6px',
                '&:hover': { backgroundColor: 'rgba(255,255,255,0.1)' }
              }}
            >
              <Box sx={{ width: 4, height: 4, backgroundColor: '#fff', borderRadius: '50%', mb: 0.5 }} />
              <Box sx={{ width: 4, height: 4, backgroundColor: '#fff', borderRadius: '50%', mb: 0.5 }} />
              <Box sx={{ width: 4, height: 4, backgroundColor: '#fff', borderRadius: '50%' }} />
            </IconButton>
          </Box>
        </Toolbar>

        <MapPdfExport
          open={pdfExportOpen}
          onClose={() => setPdfExportOpen(false)}
          visibleLayers={visibleLayers}
          measureResults={measureResults}
          pinnedPoints={pinnedPoints}
        />

      </AppBar>

      {/* Petit Min Beta Dialog */}
      <PetitMinBeta
        open={showPetitMinBeta}
        onClose={() => setShowPetitMinBeta(false)}
      />
    </>
  );
};

export default Header;
